// keyrd_console/src/components/TopBar.js
import React, { useMemo, useState } from "react";

const barStyle = {
  height: 56,
  display: "flex",
  alignItems: "center",
  padding: "0 16px",
  borderBottom: "1px solid #e5e7eb",
  backgroundColor: "#ffffff",
  boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
};

const leftStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  flex: 1,
  minWidth: 0,
};

const rightStyle = {
  display: "flex",
  alignItems: "center",
  gap: 10,
};

const labelStyle = {
  fontSize: 11,
  color: "#6b7280",
};

const inputStyle = {
  fontSize: 12,
  padding: "4px 6px",
  borderRadius: 6,
  border: "1px solid #d1d5db",
  minWidth: 200,
};

const tokenInputStyle = {
  ...inputStyle,
  minWidth: 260,
};

const buttonStyle = {
  padding: "6px 10px",
  borderRadius: 6,
  border: "none",
  backgroundColor: "#0f766e",
  color: "#ecfdf5",
  fontSize: 12,
  cursor: "pointer",
  whiteSpace: "nowrap",
};

const statusPillBase = {
  fontSize: 11,
  padding: "2px 8px",
  borderRadius: 999,
  border: "1px solid transparent",
};

function StatusPill({ status }) {
  const s = status && typeof status === "object" ? status : { state: "idle" };

  if (!s.state || s.state === "idle") {
    return (
      <span
        style={{
          ...statusPillBase,
          backgroundColor: "#f9fafb",
          color: "#6b7280",
          borderColor: "#e5e7eb",
        }}
      >
        idle
      </span>
    );
  }

  if (s.state === "pending") {
    return (
      <span
        style={{
          ...statusPillBase,
          backgroundColor: "#eff6ff",
          color: "#1d4ed8",
          borderColor: "#bfdbfe",
        }}
      >
        checking…
      </span>
    );
  }

  if (s.state === "ok") {
    return (
      <span
        style={{
          ...statusPillBase,
          backgroundColor: "#ecfdf5",
          color: "#047857",
          borderColor: "#bbf7d0",
        }}
      >
        RDN OK
      </span>
    );
  }

  if (s.state === "error") {
    const msg = typeof s.message === "string" ? s.message : "";
    return (
      <span
        style={{
          ...statusPillBase,
          backgroundColor: "#fef2f2",
          color: "#b91c1c",
          borderColor: "#fecaca",
          maxWidth: 180,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={msg || undefined}
      >
        auth error
      </span>
    );
  }

  return null;
}

function _trimOrNull(v) {
  if (v == null) return null;
  const s = String(v).trim();
  return s ? s : null;
}

function _coerceRole(json) {
  // Prefer explicit "rdn_role", fall back to "role"
  return _trimOrNull(json?.rdn_role) || _trimOrNull(json?.role) || null;
}

function TopBar({ baseUrl, token, onBaseUrlChange, onTokenChange }) {
  const [status, setStatus] = useState({ state: "idle" });
  const [rdnSummary, setRdnSummary] = useState(null);

  const canCheck = useMemo(() => {
    return Boolean(_trimOrNull(baseUrl) && _trimOrNull(token));
  }, [baseUrl, token]);

  const handleCheckRdn = async () => {
    const b = _trimOrNull(baseUrl);
    const t = _trimOrNull(token);

    if (!b || !t) {
      setStatus({ state: "error", message: "Base URL and token required" });
      setRdnSummary(null);
      return;
    }

    setStatus({ state: "pending" });
    setRdnSummary(null);

    try {
      const res = await fetch(`${b}/api/rdn/me`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${t}`,
          // Helpful for local dev CORS policies; harmless if ignored
          Accept: "application/json",
        },
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        setStatus({
          state: "error",
          message: `HTTP ${res.status} – ${String(text).slice(0, 120)}`,
        });
        setRdnSummary(null);
        return;
      }

      const json = await res.json();
      setStatus({ state: "ok" });
      setRdnSummary({
        rdn_id: _trimOrNull(json?.rdn_id),
        org_id: _trimOrNull(json?.org_id),
        role: _coerceRole(json),
        scope: _trimOrNull(json?.scope),
      });
    } catch (e) {
      setStatus({
        state: "error",
        message: (e && e.message) || String(e),
      });
      setRdnSummary(null);
    }
  };

  return (
    <header style={barStyle}>
      <div style={leftStyle}>
        <div style={{ fontWeight: 600, fontSize: 16, marginRight: 12 }}>
          KeyRD RDN Console
        </div>

        <label
          style={{
            ...labelStyle,
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <span>Base URL</span>
          <input
            type="text"
            value={baseUrl || ""}
            onChange={(e) => onBaseUrlChange && onBaseUrlChange(e.target.value)}
            placeholder="http://localhost:8000"
            style={inputStyle}
            spellCheck={false}
          />
        </label>

        <label
          style={{
            ...labelStyle,
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <span>Bearer token</span>
          <input
            type="password"
            value={token || ""}
            onChange={(e) => onTokenChange && onTokenChange(e.target.value)}
            placeholder="Paste RDN Console token"
            style={tokenInputStyle}
            spellCheck={false}
          />
        </label>
      </div>

      <div style={rightStyle}>
        {rdnSummary && (
          <div
            style={{
              fontSize: 11,
              textAlign: "right",
              color: "#4b5563",
              maxWidth: 260,
              lineHeight: 1.25,
            }}
          >
            <div>
              <strong>Org:</strong>{" "}
              <span style={{ fontFamily: "monospace" }}>
                {rdnSummary.org_id || "—"}
              </span>
            </div>
            <div>
              <strong>Role:</strong> {rdnSummary.role || "—"}
            </div>
            {rdnSummary.scope && (
              <div>
                <strong>Scope:</strong> {rdnSummary.scope}
              </div>
            )}
          </div>
        )}

        <button
          type="button"
          style={{
            ...buttonStyle,
            opacity: canCheck ? 1 : 0.75,
          }}
          onClick={handleCheckRdn}
          title={canCheck ? "Check /api/rdn/me" : "Enter Base URL and token"}
        >
          Check RDN
        </button>

        <StatusPill status={status} />
      </div>
    </header>
  );
}

export default TopBar;
